import React, { ComponentType, useMemo } from 'react';
import { Alert } from 'components/Alert';
import { connect } from 'react-redux';
import { useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import * as Yup from 'yup';
import MaskedInput from 'react-text-mask';
import styled from 'styled-components';

import { NavigationScreenProps } from 'screens/navigation';

import { jsEncrypt } from 'services/Encryption';
import { AnalyticsEvent } from 'services/AnalyticsService';
import { SUBMIT_QUESTION } from 'services/orchestration/mutations';
import useNavigationAnalytics from 'hooks/useNavigationAnalytics';

import { RootState } from 'store/types';
import {
  profileEmailsSelector,
  consumerIdSelector,
  profileConsumerSelector,
  profilePhonesSelector
} from 'store/profile/selectors';
import { Email, Consumer, Phone } from 'store/profile/types';
import { billingEncryptionSettingsSelector } from 'store/appSettings/selectors';

import { Color } from 'modules/styles/colors';
import { Spacing } from 'modules/styles/variables';
import wait from 'modules/utils/wait';
import { maskPhone } from 'modules/utils/MaskUtils';

import Divider from 'components/UI/Divider';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import { HelpPhoneLink } from 'screens/Help/styled';
import {
  MuiBox,
  MuiTextField,
  MuiContainer,
  MuiTypography,
  MuiButton,
  MuiInputAdornment
} from 'theme/material-ui';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import { CONTACT_INFO } from 'lib/constants/help';
import { TECHNICAL_SUPPORT_FIELDS, TechnicalSupportField } from './constants';
import RequiredFieldsLegend, { StyledAsterisk } from 'components/RequiredFieldsLegend';

function TextMaskCustom(props: typeof MaskedInput) {
  const { inputRef, ...other } = props;

  return <MaskedInput {...other} guide={false} />;
}

const StyledTextField = styled(MuiTextField)`
  background-color: ${Color.foreColor};
`;

const technicalSupportValidationSchema = Yup.object().shape({
  firstName: Yup.string().required('Please enter your first name'),
  lastName: Yup.string().required('Please enter your last name'),
  preferredPhone: Yup.string()
    .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone must match format (###) ###-####')
    .required('Please enter a valid phone number'),
  email: Yup.string()
    .email('Please enter a valid email')
    .notRequired(),
  issueDescription: Yup.string().required('Please explain your issue')
});

export interface Props extends NavigationScreenProps {
  consumerId: string;
  profileEmails: Email[];
  displayName: string;
  profileConsumer: Consumer;
  guarantorPhones: Phone[];
  encryptionSettings: {
    encryption_algorithm: string;
    encryption_iv: string;
    encryption_password: string;
  };
}
export interface TechnicalSupportForm {
  firstName: string;
  lastName: string;
  preferredPhone: string;
  email: string;
  issueDescription: string;
}

const initialFormValues = {
  firstName: '',
  lastName: '',
  preferredPhone: '',
  email: '',
  issueDescription: ''
};

const getInitPhone = (phones: Phone[] = []) => {
  const primary = phones.find(phone => phone.primary === true);
  const value = primary?.value || phones[0]?.value;
  return { preferredPhone: value ? maskPhone(value) : undefined } as Partial<Phone>;
};

const getInitEmail = (emails: Email[] = []) => {
  const primary = emails.find(email => email.primary === true);
  return { email: primary?.value || emails[0]?.value || undefined } as Partial<Email>;
};

const getInitUser = (consumer: Consumer) => {
  return {
    firstName: consumer.firstName,
    lastName: consumer.lastName,
    dateOfBirth: consumer.dateOfBirth
  };
};

const getInitialValues = (
  user: Partial<Consumer> | null,
  phone: Partial<Phone> | null,
  email: Partial<Email> | null
) => {
  return {
    ...initialFormValues,
    ...user,
    ...phone,
    ...email
  };
};

const fieldIcons: { [key: string]: JSX.Element } = {
  phone: (
    <MuiInputAdornment position="start" variant="outlined">
      <PhoneOutlinedIcon />
    </MuiInputAdornment>
  )
};

export const TechnicalSupportScreen = (props: Props) => {
  const [submitMutation, { loading }] = useMutation(SUBMIT_QUESTION);
  const technicalSupportFormSubmitted = useNavigationAnalytics(
    AnalyticsEvent.TechnicalSupportFormSubmitted
  );
  const technicalSupportFormCanceled = useNavigationAnalytics(
    AnalyticsEvent.TechnicalSupportFormCanceled
  );
  const serviceNumberCalled = useNavigationAnalytics(AnalyticsEvent.SupportCalled, {
    // eslint-disable-next-line @typescript-eslint/camelcase
    phone_number: CONTACT_INFO.TechSupport.phone
  });
  const {
    history,
    profileEmails,
    consumerId,
    profileConsumer,
    guarantorPhones,
    encryptionSettings
  } = props;

  const initUser = useMemo(() => {
    return profileConsumer ? getInitUser(profileConsumer) : null;
  }, [profileConsumer]);

  const initPhone = useMemo(() => {
    return guarantorPhones ? getInitPhone(guarantorPhones) : null;
  }, [guarantorPhones]);

  const initEmail = useMemo(() => {
    return profileEmails ? getInitEmail(profileEmails) : null;
  }, [profileEmails]);

  const initialValues = useMemo(() => {
    return getInitialValues(initUser, initPhone, initEmail);
  }, [initPhone]);

  const submitQuestion = async ({
    firstName,
    lastName,
    preferredPhone,
    email,
    issueDescription
  }: TechnicalSupportForm) => {
    const questionDTO = {
      firstName,
      lastName,
      preferredPhone,
      email,
      issueDescription,
      MRN: consumerId
    };

    const {
      encryption_algorithm: algorithm,
      encryption_password: pass,
      encryption_iv: IV
    } = encryptionSettings;

    const encryptedData = jsEncrypt(algorithm, pass, IV, JSON.stringify(questionDTO));
    try {
      await submitMutation({
        variables: { encryptedIncident: encryptedData },
        context: {
          clientName: 'orchestration'
        }
      });
      await wait(60);
      Alert.alert('Message Sent', 'We will contact you within 2 business days', [
        {
          text: 'Okay',
          onPress: () => {
            history.goBack();
          }
        }
      ]);
      technicalSupportFormSubmitted();
    } catch (err) {
      await wait(60);
      Alert.alert('Question did not submit.', 'Sorry for the inconvenience.', [
        {
          text: 'Try Again?',
          onPress: () =>
            submitQuestion({ firstName, lastName, preferredPhone, email, issueDescription })
        },
        { text: 'Cancel' }
      ]);
    }
  };

  const phoneHref = `tel:${CONTACT_INFO.TechSupport.phone}`;

  const supportHours = CONTACT_INFO.TechSupport.subtitle.split('\n').map(line => {
    return <MuiTypography key={line}>{line}</MuiTypography>;
  });

  return (
    <MuiBox bgcolor={Color.baseColor} flex={1} width="100%" height="100%">
      <SpinnerModal isLoading={loading} />
      <MuiContainer maxWidth="md">
        <MuiBox
          py={Spacing.small}
          display="flex"
          flex={1}
          flexDirection="row"
          justifyContent="space-between"
        >
          <MuiTypography variant="h5">Call us</MuiTypography>
          <MuiBox display="flex" flexDirection="column" alignItems="flex-end">
            <MuiBox display="flex" flex={1} flexDirection="row" alignItems="center">
              {fieldIcons.phone}
              <HelpPhoneLink
                data-testid="service-number"
                href={phoneHref}
                onClick={serviceNumberCalled}
              >
                <MuiTypography variant="h6" color={Color.link}>
                  {CONTACT_INFO.TechSupport.phone}
                </MuiTypography>
              </HelpPhoneLink>
            </MuiBox>
            <MuiTypography align="right">{supportHours}</MuiTypography>
          </MuiBox>
        </MuiBox>

        <Divider />
        <MuiBox
          py={Spacing.small}
          display="flex"
          flex={1}
          flexDirection="row"
          justifyContent="space-between"
        >
          <MuiTypography variant="h5">Send a message</MuiTypography>
          <MuiBox maxWidth="40%">
            <MuiTypography align="right">Response within 2 business days</MuiTypography>
          </MuiBox>
        </MuiBox>
        <Formik
          initialValues={initialValues}
          validationSchema={technicalSupportValidationSchema}
          onSubmit={values => submitQuestion(values)}
        >
          {({
            values,
            touched,
            errors,
            handleBlur,
            isValid,
            handleSubmit,
            setFieldValue,
            dirty
          }) => {
            const length = values.issueDescription ? values.issueDescription.length : 0;

            const getFormError = (formKey: keyof typeof values) => {
              return touched[formKey] ? errors[formKey] || null : null;
            };

            return (
              <MuiContainer maxWidth="sm">
                <MuiBox display="flex" flexDirection="row">
                  <RequiredFieldsLegend />
                </MuiBox>
                <MuiBox pt={Spacing.small}>
                  {TECHNICAL_SUPPORT_FIELDS.map((field: TechnicalSupportField) => {
                    const { key, props: itemProps } = field;
                    const {
                      icon,
                      maxLength,
                      fieldHeight,
                      accessibilityLabel,
                      label,
                      ...rest
                    } = itemProps;
                    const customProps: any = {
                      inputProps: {
                        style: { minHeight: fieldHeight },
                        'aria-label': accessibilityLabel
                      }
                    };
                    if (maxLength) {
                      customProps.inputProps.maxLength = maxLength;
                    }
                    if (icon) {
                      customProps.startAdornment = fieldIcons[icon];
                    }
                    return (
                      <MuiBox pb={Spacing.small} key={key}>
                        <MuiBox display="flex" flexDirection="row" pb={1}>
                          <MuiTypography>{label}</MuiTypography>
                          <MuiBox px={1}>
                            <StyledAsterisk />
                          </MuiBox>
                        </MuiBox>
                        <StyledTextField
                          {...rest}
                          InputProps={{
                            ...customProps
                          }}
                          FormHelperTextProps={{ style: { position: 'absolute', top: '100%' } }}
                          variant="outlined"
                          value={values[key]}
                          onChange={e => {
                            const { value } = e.target;
                            if (key === 'preferredPhone') {
                              setFieldValue(key, maskPhone(value));
                            } else {
                              setFieldValue(key, value);
                            }
                          }}
                          error={!!getFormError(key)}
                          helperText={getFormError(key)}
                          onBlur={handleBlur(key)}
                          fullWidth
                        />
                        {key === 'issueDescription' ? (
                          <MuiBox pt={Spacing.small}>
                            <MuiTypography variant="body1" color={Color.gray}>
                              {length}/8192
                            </MuiTypography>
                          </MuiBox>
                        ) : null}
                      </MuiBox>
                    );
                  })}
                </MuiBox>
                <MuiBox width="100%" p={Spacing.small} borderColor={Color.grayLight} borderTop={1}>
                  <MuiButton
                    data-testid="submit-button"
                    color="primary"
                    variant="contained"
                    fullWidth
                    disabled={!isValid || !dirty}
                    onClick={() => handleSubmit()}
                  >
                    Submit
                  </MuiButton>
                  <MuiButton
                    data-testid="cancel-btn"
                    fullWidth
                    variant="text"
                    onClick={() => {
                      technicalSupportFormCanceled();
                      history.goBack();
                    }}
                  >
                    Cancel
                  </MuiButton>
                </MuiBox>
              </MuiContainer>
            );
          }}
        </Formik>
      </MuiContainer>
    </MuiBox>
  );
};

const mapStateToProps = (state: RootState) => ({
  profileEmails: profileEmailsSelector(state),
  consumerId: consumerIdSelector(state),
  profileConsumer: profileConsumerSelector(state),
  guarantorPhones: profilePhonesSelector(state),
  encryptionSettings: billingEncryptionSettingsSelector(state)
});

export default connect(mapStateToProps)(TechnicalSupportScreen as ComponentType);
